import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  FiAlertOctagon, FiAlertTriangle, FiArrowDown, FiArrowUp, FiCheckCircle, FiChevronDown, FiChevronRight, FiClock,
  FiHelpCircle, FiSearch, FiShare2, FiStar, FiX,
} from "react-icons/fi";

import { api, graphTargets, portGraph, type GraphTargetDevice, type GraphWindow } from "@/lib/api";
import { formatBps } from "@/lib/format";
import { GraphImg } from "@/components/GraphImg";
import { useNow } from "@/components/MonitoringBanner";
import { durText } from "@/components/OntStatus";
import { fmtDateTime, fmtNum, useI18n, type Key } from "@/lib/i18n";
import {
  aboveSince, capacity, fmtBytes, gaps, grade, isLag, pct, portState, segments,
  type Grade, type PortState, type TrafficPoint, type TrafficResponse,
} from "@/lib/traffic";
import { usePref } from "@/lib/prefs";
import { cn } from "@/lib/utils";

const WINDOWS: GraphWindow[] = ["1h", "6h", "24h", "7d", "30d"];
type View = "librenms" | "native";

// ---------- favourites / recents (per account) ----------

type Ref = { device: number; port: number; label: string; sub: string };

function useRefs() {
  const [favs, setFavs] = usePref<Ref[]>("traffic.favs", [], "nocops.traffic.favs");
  const [recent, setRecent, recentLoaded] = usePref<Ref[]>("traffic.recent", [], "nocops.traffic.recent");
  const same = (a: Ref, b: Pick<Ref, "device" | "port">) => a.device === b.device && a.port === b.port;
  return {
    favs, recent, recentLoaded,
    isFav: (r: Pick<Ref, "device" | "port">) => favs.some((f) => same(f, r)),
    toggleFav: (r: Ref) => setFavs(favs.some((f) => same(f, r)) ? favs.filter((f) => !same(f, r)) : [...favs, r]),
    visit: (r: Ref) => {
      if (recent[0] && same(recent[0], r)) return; // no write for re-opening the same port
      setRecent([r, ...recent.filter((x) => !same(x, r))].slice(0, 6));
    },
  };
}

// ---------- vocabulary ----------

const STATE_UI: Record<PortState, { icon: typeof FiCheckCircle; text: string; bg: string }> = {
  up: { icon: FiCheckCircle, text: "text-signal-good", bg: "bg-signal-good/10" },
  degraded: { icon: FiAlertTriangle, text: "text-signal-warn", bg: "bg-amber-400/10" },
  down: { icon: FiAlertOctagon, text: "text-white", bg: "bg-signal-crit" },
  stale: { icon: FiClock, text: "text-amber-200", bg: "bg-amber-400/10" },
  unknown: { icon: FiHelpCircle, text: "text-slate-300", bg: "bg-slate-700/40" },
};

function StateBadge({ s }: { s: PortState }) {
  const { t } = useI18n();
  const u = STATE_UI[s];
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-[11px] font-semibold tracking-[0.12em] uppercase", u.text, u.bg)}>
      <u.icon className="size-3.5" /> {t(`tr.state.${s}` as Key)}
    </span>
  );
}

const GRADE_TEXT: Record<Grade, string> = { normal: "text-signal-good", high: "text-signal-warn", critical: "text-signal-crit" };
const GRADE_BAR: Record<Grade, string> = { normal: "bg-signal", high: "bg-signal-warn", critical: "bg-signal-crit" };

// ---------- page ----------

export default function GraphsPage() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const all = useQuery({ queryKey: ["graph-targets", ""], queryFn: () => graphTargets("") });
  const devices = all.data?.devices ?? [];
  const deviceId = Number(params.get("device") ?? 0);
  const portId = Number(params.get("port") ?? 0);
  const win = (WINDOWS.includes(params.get("window") as GraphWindow) ? params.get("window") : "24h") as GraphWindow;
  const view = (params.get("view") === "native" ? "native" : "librenms") as View;
  const compare = params.get("compare") === "prev";
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v === null ? p.delete(k) : p.set(k, v));
    setParams(p, { replace: true });
  };
  const device = devices.find((d) => d.device_id === deviceId);
  const port = device?.ports.find((p) => p.port_id === portId);

  // Arriving with only a device (Link Map, Topology): open its busiest port.
  // Arriving with nothing: the busiest port overall.
  useEffect(() => {
    if (!devices.length || port) return;
    const pool = device ? [device] : devices;
    let best: { d: number; p: number; r: number } | null = null;
    for (const d of pool) for (const p of d.ports) {
      const r = Math.max(p.rx_bps ?? 0, p.tx_bps ?? 0);
      if (!best || r > best.r) best = { d: d.device_id, p: p.port_id, r };
    }
    if (best) set({ device: String(best.d), port: String(best.p) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices.length, deviceId, portId]);

  const refs = useRefs();
  const tr = useQuery({
    queryKey: ["traffic", deviceId, portId, win, compare],
    queryFn: () => api<TrafficResponse>(`/api/v1/traffic?device_id=${deviceId}&port_id=${portId}&window=${win}${compare ? "&compare=prev" : ""}`),
    enabled: !!deviceId && !!portId,
    refetchInterval: 60_000,
  });
  const here = port && device ? { device: device.device_id, port: port.port_id, label: port.ifalias || port.ifname, sub: `${device.name} · ${port.ifname}` } : null;
  const visited = useRef("");
  useEffect(() => {
    if (!here || !refs.recentLoaded || visited.current === `${here.device}:${here.port}`) return;
    visited.current = `${here.device}:${here.port}`;
    refs.visit(here);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [here?.device, here?.port, refs.recentLoaded]);

  if (all.data && !all.data.librenms_configured) {
    return <p className="rounded-lg border border-rule bg-panel px-4 py-6 text-slate-400">{t("common.librenmsNotConfigured")}</p>;
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="eyebrow">{t("tr.title")}</span>
        <SearchBox onPick={(d, p) => set({ device: String(d), port: String(p) })} />
        <Shortcuts refs={refs} current={here} onPick={(r) => set({ device: String(r.device), port: String(r.port) })} />
      </div>
      {!deviceId || !portId ? <p className="text-slate-400">{all.isLoading ? t("common.loading") : t("graphs.pick")}</p> : (
        <Workspace tr={tr.data} loading={tr.isLoading} error={tr.error as Error | null} device={device} win={win} view={view} compare={compare}
          fav={here ? refs.isFav(here) : false} onFav={() => here && refs.toggleFav(here)} set={set} />
      )}
    </div>
  );
}

// ---------- search ----------

function SearchBox({ onPick }: { onPick: (device: number, port: number) => void }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => { const id = setTimeout(() => setDq(q.trim()), 250); return () => clearTimeout(id); }, [q]);
  const res = useQuery({ queryKey: ["graph-targets", dq], queryFn: () => graphTargets(dq), enabled: dq.length >= 2 });
  const hits = (res.data?.devices ?? []).flatMap((d) => d.ports.map((p) => ({ d, p }))).slice(0, 40);
  const pick = (d: number, p: number) => { onPick(d, p); setOpen(false); setQ(""); };
  return (
    <div className="relative w-full max-w-md">
      <FiSearch className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-slate-500" />
      <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Enter" && hits[0]) pick(hits[0].d.device_id, hits[0].p.port_id); if (e.key === "Escape") setOpen(false); }}
        placeholder={t("tr.search")} aria-label={t("tr.search")}
        className="h-8 w-full rounded-md border border-rule bg-panel pr-8 pl-8 text-[12.5px] text-slate-100 outline-none placeholder:text-slate-500 focus:border-signal/50" />
      {q && <button type="button" onClick={() => setQ("")} aria-label={t("common.close")} className="absolute top-1/2 right-2 -translate-y-1/2 text-slate-500 hover:text-slate-200"><FiX /></button>}
      {open && dq.length >= 2 && (
        <div className="absolute z-30 mt-1 max-h-96 w-[34rem] overflow-y-auto rounded-md border border-rule bg-[#070b14] py-1 shadow-2xl">
          {hits.length === 0 && <p className="px-3 py-2 text-[12.5px] text-slate-500">{res.isFetching ? t("common.loading") : t("tr.noHits")}</p>}
          {hits.map(({ d, p }) => (
            <button key={`${d.device_id}:${p.port_id}`} type="button" onClick={() => pick(d.device_id, p.port_id)}
              className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-3 py-1.5 text-left hover:bg-white/[0.04]">
              <span className="min-w-0">
                <span className="block truncate text-[13px] text-slate-100"><span className="data">{p.ifname}</span>{p.ifalias && <span className="text-slate-400"> · {p.ifalias}</span>}</span>
                <span className="block truncate text-[11.5px] text-slate-500">{d.name} · {d.hostname}</span>
              </span>
              <span className="data text-right text-[11px] text-slate-500">↓{formatBps(p.rx_bps)} ↑{formatBps(p.tx_bps)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Shortcuts({ refs, current, onPick }: { refs: ReturnType<typeof useRefs>; current: Ref | null; onPick: (r: Ref) => void }) {
  const { t } = useI18n();
  const recent = refs.recent.filter((r) => !refs.isFav(r) && !(current && r.device === current.device && r.port === current.port)).slice(0, 4);
  if (!refs.favs.length && !recent.length) return null;
  const chip = (r: Ref, fav: boolean) => (
    <button key={`${fav}:${r.device}:${r.port}`} type="button" onClick={() => onPick(r)} title={r.sub}
      className={cn("inline-flex max-w-44 items-center gap-1 truncate rounded-sm border px-2 py-0.5 text-[11.5px]",
        current && r.device === current.device && r.port === current.port ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400 hover:text-slate-100")}>
      {fav && <FiStar className="size-3 shrink-0 fill-amber-300 text-amber-300" />}<span className="truncate">{r.label}</span>
    </button>
  );
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      {refs.favs.map((r) => chip(r, true))}
      {recent.length > 0 && <span className="ml-1 text-[10.5px] tracking-[0.12em] text-slate-600 uppercase">{t("tr.recent")}</span>}
      {recent.map((r) => chip(r, false))}
    </div>
  );
}

// ---------- workspace ----------

function Workspace({ tr, loading, error, device, win, view, compare, fav, onFav, set }: {
  tr: TrafficResponse | undefined; loading: boolean; error: Error | null; device: GraphTargetDevice | undefined; win: GraphWindow;
  view: View; compare: boolean; fav: boolean; onFav: () => void; set: (n: Record<string, string | null>) => void;
}) {
  const { t, locale } = useI18n();
  const now = useNow(15_000);
  if (loading) return <p className="text-slate-400">{t("common.loading")}</p>;
  if (error || !tr) return <p className="text-signal-crit">{error?.message ?? t("tr.noData")}</p>;
  const p = tr.port;
  const state = portState(tr, now);
  const cap = capacity(tr);
  const inPct = pct(p.rx_bps, cap), outPct = pct(p.tx_bps, cap);
  const inG = grade(inPct, tr.thresholds), outG = grade(outPct, tr.thresholds);
  const polledAgo = p.polled_at ? now - Date.parse(p.polled_at) : null;
  const live = state !== "down" && state !== "stale";
  const last = [...tr.series].reverse().find((x) => x.in !== null || x.out !== null);
  const critBps = cap ? (cap * tr.thresholds.crit_pct) / 100 : null, highBps = cap ? (cap * tr.thresholds.high_pct) / 100 : null;
  const hot = live && (inG === "critical" || outG === "critical" ? "critical" : inG === "high" || outG === "high" ? "high" : null);
  const hotDir: "in" | "out" = (inPct ?? 0) >= (outPct ?? 0) ? "in" : "out";
  const hotSince = hot && (hot === "critical" ? critBps : highBps) ? aboveSince(tr.series, hotDir, (hot === "critical" ? critBps : highBps)!) : null;

  return (
    <div className="space-y-3">
      {/* Identity: what, where, whether it is up, how big, how fresh. */}
      <header className="flex flex-wrap items-end gap-x-6 gap-y-2">
        <div className="min-w-0">
          <p className="truncate text-[13px] text-slate-400">
            <Link to={`/topology?locate=${encodeURIComponent(tr.device.key)}`} className="hover:text-signal">{tr.device.name}</Link>
            <span className="data text-slate-600"> · {tr.device.hostname}</span>
          </p>
          <h1 className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[22px] leading-tight font-semibold tracking-tight text-slate-50">
            <span className="data">{p.ifname}</span>
            {p.ifalias && <span className="text-[17px] font-normal text-slate-300">{p.ifalias}</span>}
            <button type="button" onClick={onFav} aria-pressed={fav} aria-label={t(fav ? "tr.unfav" : "tr.fav")} title={t(fav ? "tr.unfav" : "tr.fav")}
              className="text-slate-500 hover:text-amber-300"><FiStar className={cn("size-4", fav && "fill-amber-300 text-amber-300")} /></button>
          </h1>
        </div>
        <div className="flex items-center gap-3 pb-1">
          <StateBadge s={state} />
          <span className="data text-[14px] text-slate-200">{formatBps(p.speed_bps)}</span>
          {isLag(p.if_type) && <span className="rounded-sm border border-rule px-1.5 text-[10.5px] font-semibold tracking-[0.12em] text-slate-300 uppercase">LAG</span>}
          {device && device.ports.length > 1 && (
            <select value={p.port_id} onChange={(e) => set({ port: e.target.value })} aria-label={t("tr.interface")}
              className="h-7 max-w-64 rounded-md border border-rule bg-panel px-2 text-[12px] text-slate-300">
              {device.ports.map((x) => <option key={x.port_id} value={x.port_id}>{x.ifname}{x.ifalias ? ` — ${x.ifalias}` : ""}</option>)}
            </select>
          )}
        </div>
        <div className="ml-auto flex items-center gap-3 pb-1 text-[12px]">
          <span className={cn("inline-flex items-center gap-1.5", live ? "text-slate-300" : "text-amber-200")} title={p.polled_at ? fmtDateTime(locale, p.polled_at) : undefined}>
            <span className={cn("size-1.5 rounded-full", live ? "animate-pulse bg-signal-good" : "bg-signal-warn")} />
            <span className="font-semibold tracking-[0.1em] uppercase">{t(live ? "tr.live" : state === "down" ? "tr.state.down" : "tr.stale")}</span>
            <span className="data text-slate-400">{polledAgo !== null ? t("tr.polled", { age: durText(polledAgo), every: durText(p.poll_period_s * 1000) }) : t("tr.neverPolled")}</span>
          </span>
          <span className="text-[11px] text-slate-600">{t("tr.source")}</span>
        </div>
      </header>

      {/* The one line to read before the graph. */}
      {state === "down" && <Banner tone="crit" icon={FiAlertOctagon}>
        {t("tr.down", { since: tr.down_since ? durText(now - Date.parse(tr.down_since)) : t("tr.unknownSince") })}
        {last && <span className="text-rose-200/80"> · {t("tr.lastTraffic", { in: formatBps(last.in), out: formatBps(last.out), at: durText(now - Date.parse(last.ts)) })}</span>}
      </Banner>}
      {state === "stale" && <Banner tone="warn" icon={FiClock}>{t("tr.staleBody", { age: polledAgo !== null ? durText(polledAgo) : "?", every: durText(p.poll_period_s * 1000) })}</Banner>}
      {hot && <Banner tone={hot === "critical" ? "crit" : "warn"} icon={FiAlertTriangle}>
        {t(`tr.hot.${hotDir}` as Key, { pct: (hot === "critical" ? tr.thresholds.crit_pct : tr.thresholds.high_pct).toFixed(0) })}
        {hotSince && <span className="opacity-80"> · {t("tr.since", { age: durText(now - Date.parse(hotSince)) })}</span>}
        {tr.stats[hotDir].peak !== null && cap && <span className="opacity-80"> · {t("tr.peakPct", { v: formatBps(tr.stats[hotDir].peak), pct: ((tr.stats[hotDir].peak! / cap) * 100).toFixed(1) })}</span>}
      </Banner>}
      {state !== "down" && tr.lag && tr.lag.up < tr.lag.total && <Banner tone="warn" icon={FiAlertTriangle}>
        {t("tr.lagDegraded", { up: tr.lag.up, total: tr.lag.total, avail: formatBps(tr.lag.capacity_up_bps), all: formatBps(tr.lag.capacity_total_bps) })}
      </Banner>}

      {/* Current traffic: direction, rate, share of live capacity. */}
      <div className="grid overflow-hidden rounded-lg border border-rule bg-panel md:grid-cols-[1fr_1fr_0.8fr_0.7fr]">
        <DirCell dir="in" bps={live ? p.rx_bps : null} pctV={live ? inPct : null} g={live ? inG : null} stale={!live} />
        <DirCell dir="out" bps={live ? p.tx_bps : null} pctV={live ? outPct : null} g={live ? outG : null} stale={!live} />
        <Cell label={t("tr.capacity")}>
          <div className="data text-[22px] leading-tight text-slate-100">{formatBps(cap)}</div>
          <div className="text-[11.5px] text-slate-500">
            {tr.lag ? t("tr.members", { up: tr.lag.up, total: tr.lag.total, each: formatBps(tr.lag.members[0]?.speed_bps) }) : isLag(p.if_type) ? t("tr.lagNoMembers") : t("tr.ifSpeed")}
          </div>
        </Cell>
        <Cell label={t("tr.errors")}>
          <div className={cn("data text-[22px] leading-tight", (p.in_errors_rate ?? 0) + (p.out_errors_rate ?? 0) > 0 ? "text-signal-warn" : "text-slate-100")}>
            {p.in_errors_rate === null && p.out_errors_rate === null ? "—" : fmtRate((p.in_errors_rate ?? 0) + (p.out_errors_rate ?? 0))}
          </div>
          <div className="text-[11.5px] text-slate-500">{t("tr.errorsSub", { in: fmtRate(p.in_errors_rate), out: fmtRate(p.out_errors_rate) })}</div>
        </Cell>
      </div>

      {/* History: range, statistics, graph. */}
      <section className="rounded-lg border border-rule bg-panel">
        <div className="flex flex-wrap items-center gap-3 border-b border-rule px-4 py-2">
          <h2 className="eyebrow">{t("tr.history", { win })}</h2>
          <div role="tablist" className="flex rounded-sm border border-rule p-0.5">
            {WINDOWS.map((w) => (
              <button key={w} role="tab" type="button" aria-selected={win === w} onClick={() => set({ window: w })}
                className={cn("data rounded-sm px-2.5 py-0.5 text-[12px]", win === w ? "bg-signal/15 font-semibold text-slate-100" : "text-slate-400 hover:text-slate-100")}>{w}</button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2 text-[12px]">
            {view === "native" && (
              <label className="flex items-center gap-1.5 text-slate-400">
                <input type="checkbox" className="accent-cyan-400" checked={compare} onChange={(e) => set({ compare: e.target.checked ? "prev" : null })} />
                {t("tr.compare")}
              </label>
            )}
            <div className="flex rounded-sm border border-rule p-0.5">
              {(["librenms", "native"] as View[]).map((v) => (
                <button key={v} type="button" aria-pressed={view === v} onClick={() => set(v === "librenms" ? { view: null, compare: null } : { view: v })}
                  className={cn("rounded-sm px-2 py-0.5", view === v ? "bg-signal/10 text-slate-100" : "text-slate-400 hover:text-slate-100")}>{t(`tr.view.${v}` as Key)}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="grid gap-4 p-4 2xl:grid-cols-[minmax(0,1fr)_26rem]">
          <div className="min-w-0">
            {view === "librenms"
              ? <LegacyGraph device={tr.device.librenms_id} port={p.port_id} win={win} alt={`${tr.device.name} · ${p.ifname} · ${win}`} />
              : <NativeGraph tr={tr} cap={cap} />}
          </div>
          <Stats tr={tr} cap={cap} />
        </div>
      </section>

      <div className="grid items-start gap-3 xl:grid-cols-2">
        {tr.lag && <Members lag={tr.lag} />}
        <Details tr={tr} state={state} />
      </div>
    </div>
  );
}

const fmtRate = (v: number | null | undefined) => (v === null || v === undefined ? "—" : v === 0 ? "0/s" : `${v < 10 ? v.toFixed(1) : Math.round(v)}/s`);

function Banner({ tone, icon: I, children }: { tone: "crit" | "warn"; icon: typeof FiClock; children: ReactNode }) {
  return (
    <div className={cn("flex items-center gap-2 rounded-md border px-3 py-2 text-[13px]",
      tone === "crit" ? "border-signal-crit/50 bg-signal-crit/10 text-rose-100" : "border-amber-400/40 bg-amber-400/[0.07] text-amber-100")}>
      <I className={cn("size-4 shrink-0", tone === "crit" ? "text-signal-crit" : "text-signal-warn")} />
      <span>{children}</span>
    </div>
  );
}

const Cell = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="border-b border-rule px-4 py-2.5 md:border-r md:border-b-0 md:last:border-r-0">
    <div className="eyebrow mb-0.5 text-[10px]">{label}</div>{children}
  </div>
);

function DirCell({ dir, bps, pctV, g, stale }: { dir: "in" | "out"; bps: number | null | undefined; pctV: number | null; g: Grade | null; stale: boolean }) {
  const { t } = useI18n();
  const Arrow = dir === "in" ? FiArrowDown : FiArrowUp;
  return (
    <Cell label={t(`tr.dir.${dir}` as Key)}>
      <div className="flex items-baseline gap-3">
        <span className="flex items-baseline gap-1.5">
          <Arrow className={cn("size-4 self-center", dir === "in" ? "text-signal" : "text-violet-300")} />
          <span className="data text-[26px] leading-tight text-slate-50">{stale ? "—" : formatBps(bps)}</span>
        </span>
        {pctV !== null && <span className={cn("data text-[15px]", g ? GRADE_TEXT[g] : "text-slate-400")}>{pctV.toFixed(1)}%</span>}
        {g && <span className={cn("text-[10.5px] font-semibold tracking-[0.12em] uppercase", GRADE_TEXT[g])}>{t(`tr.grade.${g}` as Key)}</span>}
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-rule" aria-hidden>
        {pctV !== null && g && <div className={cn("h-full", GRADE_BAR[g])} style={{ width: `${Math.min(100, pctV)}%` }} />}
      </div>
      {stale && <div className="mt-1 text-[11px] text-amber-200/90">{t("tr.notCurrent")}</div>}
    </Cell>
  );
}

// ---------- graphs ----------

/**
 * The LibreNMS image, framed as what it is: an embedded rendering from
 * another system. Aspect preserved (4:1 at a whitelisted width); no CSS
 * filters -- inverting it would ruin its colours and legend.
 */
function LegacyGraph({ device, port, win, alt }: { device: number; port: number; win: GraphWindow; alt: string }) {
  const { t } = useI18n();
  return (
    <figure className="rounded-md border border-rule bg-[#0b111c] p-2">
      <GraphImg queryKey={["port-graph", device, port, win]} fetch={(w) => portGraph(device, port, win, w)} alt={alt} />
      <figcaption className="mt-1.5 flex items-center justify-between px-0.5 text-[11px] text-slate-500">
        <span>{t("tr.legacyCaption")}</span><span>{t("tr.legacyNote")}</span>
      </figcaption>
    </figure>
  );
}

/**
 * Native graph from the panel's own samples: inbound and outbound both
 * positive on one axis (easier to compare than LibreNMS's mirrored chart),
 * capacity as a line, missing polls as visible NO DATA bands, never zeros.
 */
function NativeGraph({ tr, cap }: { tr: TrafficResponse; cap: number | null }) {
  const { t, locale } = useI18n();
  const [hover, setHover] = useState<TrafficPoint | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  // The drawing is as wide as its box: a fixed viewBox letterboxes on wide
  // screens and squashes the labels on narrow ones.
  const [W, setW] = useState(1000);
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(400, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [tr.series.length > 0]);
  const s = tr.series;
  const t0 = Date.parse(tr.from), t1 = Date.parse(tr.to);
  const peak = Math.max(0, ...s.flatMap((p) => [p.in ?? 0, p.out ?? 0]), ...(tr.compare?.series ?? []).flatMap((p) => [p.in ?? 0, p.out ?? 0]));
  // Scale to the traffic; bring capacity into view once traffic is within
  // half of it (a 1 Gbps flow on a 500 Gbps LAG must still be readable).
  const top = niceMax(cap && peak > cap * 0.5 ? Math.max(cap * 1.02, peak) : peak * 1.15 || 1);
  const H = 280, L = 64, B = 22, T = 8;
  const x = (ts: string) => L + ((Date.parse(ts) - t0) / (t1 - t0)) * (W - L - 8);
  const y = (v: number) => T + (1 - v / top) * (H - T - B);
  const segs = segments(s, tr.step_s);
  const holes = gaps(s, tr.step_s, tr.from, tr.to);
  const line = (pts: TrafficPoint[], dir: "in" | "out") => pts.filter((p) => p[dir] !== null).map((p) => `${x(p.ts).toFixed(1)},${y(p[dir]!).toFixed(1)}`).join(" ");
  const area = (pts: TrafficPoint[]) => {
    const v = pts.filter((p) => p.in !== null);
    return v.length ? `M${x(v[0].ts)},${y(0)} L${line(v, "in").replace(/ /g, " L")} L${x(v[v.length - 1].ts)},${y(0)} Z` : "";
  };
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * top);
  const lines = cap ? [
    { v: cap, label: t("tr.capacityLine", { v: formatBps(cap) }), cls: "stroke-slate-300", dash: "" },
    ...(cap * tr.thresholds.crit_pct / 100 <= top && peak > cap * tr.thresholds.high_pct / 100 * 0.8 ? [{ v: cap * tr.thresholds.crit_pct / 100, label: `${tr.thresholds.crit_pct}%`, cls: "stroke-signal-crit", dash: "4 4" }] : []),
    ...(cap * tr.thresholds.high_pct / 100 <= top && peak > cap * tr.thresholds.high_pct / 100 * 0.8 ? [{ v: cap * tr.thresholds.high_pct / 100, label: `${tr.thresholds.high_pct}%`, cls: "stroke-signal-warn", dash: "4 4" }] : []),
  ].filter((l) => l.v <= top) : [];
  const onMove = (e: React.MouseEvent) => {
    const r = svgRef.current!.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    let best: TrafficPoint | null = null, bd = Infinity;
    for (const p of s) { const d = Math.abs(x(p.ts) - px); if (d < bd) { bd = d; best = p; } }
    setHover(bd < 24 ? best : null);
  };
  if (!s.length) {
    return (
      <div className="grid h-72 place-items-center rounded-md border border-dashed border-rule text-center text-[13px] text-slate-400">
        <div><p>{t("tr.nativeEmpty")}</p><p className="mt-1 text-[12px] text-slate-500">{t("tr.nativeEmptyHint")}</p></div>
      </div>
    );
  }
  const ticksX = xTicks(t0, t1, locale);
  return (
    <div className="relative">
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-[280px] w-full select-none" onMouseMove={onMove} onMouseLeave={() => setHover(null)} role="img" aria-label={t("tr.nativeAria")}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={L} x2={W - 8} y1={y(v)} y2={y(v)} className="stroke-rule" strokeWidth={1} />
            <text x={L - 6} y={y(v) + 3} textAnchor="end" className="fill-slate-500 text-[10px]" fontFamily="var(--font-mono, monospace)">{formatBps(v)}</text>
          </g>
        ))}
        {ticksX.map((tk) => <text key={tk.ms} x={L + ((tk.ms - t0) / (t1 - t0)) * (W - L - 8)} y={H - 6} textAnchor="middle" className="fill-slate-500 text-[10px]">{tk.label}</text>)}
        {holes.map((g, i) => (
          <g key={i}>
            <rect x={x(g.from)} y={T} width={Math.max(1, x(g.to) - x(g.from))} height={H - T - B} className="fill-slate-500/10" />
            {x(g.to) - x(g.from) > 60 && <text x={(x(g.from) + x(g.to)) / 2} y={T + 14} textAnchor="middle" className="fill-slate-500 text-[10px] tracking-[0.12em]">{t("tr.noDataBand")}</text>}
          </g>
        ))}
        {lines.map((l) => (
          <g key={l.label}>
            <line x1={L} x2={W - 8} y1={y(l.v)} y2={y(l.v)} className={l.cls} strokeWidth={1} strokeDasharray={l.dash} opacity={0.7} />
            <text x={W - 10} y={y(l.v) - 3} textAnchor="end" className="fill-slate-400 text-[10px]">{l.label}</text>
          </g>
        ))}
        {tr.compare && segments(tr.compare.series, tr.step_s).map((sg, i) => (
          <g key={`c${i}`} opacity={0.45}>
            <polyline points={line(sg, "in")} fill="none" className="stroke-signal" strokeWidth={1} strokeDasharray="3 3" />
            <polyline points={line(sg, "out")} fill="none" className="stroke-violet-300" strokeWidth={1} strokeDasharray="3 3" />
          </g>
        ))}
        {segs.map((sg, i) => (
          <g key={i}>
            <path d={area(sg)} className="fill-signal/15" />
            <polyline points={line(sg, "in")} fill="none" className="stroke-signal" strokeWidth={1.6} />
            <polyline points={line(sg, "out")} fill="none" className="stroke-violet-300" strokeWidth={1.6} />
          </g>
        ))}
        {hover && <line x1={x(hover.ts)} x2={x(hover.ts)} y1={T} y2={H - B} className="stroke-slate-400" strokeWidth={1} strokeDasharray="2 2" />}
      </svg>
      {hover && (
        <div className="pointer-events-none absolute top-2 z-10 w-64 rounded-md border border-rule bg-[#070b14]/95 px-3 py-2 text-[12px] shadow-xl"
          style={{ left: `${Math.min(78, (x(hover.ts) / W) * 100)}%` }}>
          <div className="data mb-1 text-slate-400">{fmtDateTime(locale, hover.ts).replace(/:\d\d$/, "")}</div>
          <HoverRow arrow="↓" label={t("tr.dir.in")} v={hover.in} cap={cap} cls="text-signal" />
          <HoverRow arrow="↑" label={t("tr.dir.out")} v={hover.out} cap={cap} cls="text-violet-300" />
          {cap && <div className="mt-1 text-slate-500">{t("tr.capacity")} <span className="data text-slate-300">{formatBps(cap)}</span></div>}
        </div>
      )}
      <div className="mt-1 flex flex-wrap gap-x-4 px-1 text-[11px] text-slate-400">
        <span><span className="text-signal">━</span> {t("tr.dir.in")}</span>
        <span><span className="text-violet-300">━</span> {t("tr.dir.out")}</span>
        {tr.compare && <span><span className="opacity-50">┅</span> {t("tr.prevPeriod")}</span>}
        <span className="ml-auto text-slate-500">{t("tr.nativeNote", { step: durText(tr.step_s * 1000) })}</span>
      </div>
    </div>
  );
}

function HoverRow({ arrow, label, v, cap, cls }: { arrow: string; label: string; v: number | null; cap: number | null; cls: string }) {
  const { t } = useI18n();
  return (
    <div className="flex justify-between gap-2 whitespace-nowrap">
      <span className={cls}>{arrow} {label}</span>
      <span className="data text-slate-100">{v === null ? t("tr.noDataShort") : formatBps(v)}{v !== null && cap ? <span className="text-slate-500"> · {((v / cap) * 100).toFixed(1)}%</span> : null}</span>
    </div>
  );
}

/** A tidy axis maximum: 1, 2, 2.5 or 5 times a power of ten. */
function niceMax(v: number): number {
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

function xTicks(t0: number, t1: number, locale: string) {
  const span = t1 - t0, n = 6;
  const day = span > 36 * 3600_000;
  return Array.from({ length: n + 1 }, (_, i) => {
    const ms = t0 + (span * i) / n;
    const d = new Date(ms);
    return { ms, label: day ? d.toLocaleDateString(locale, { day: "2-digit", month: "short" }) : d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) };
  });
}

// ---------- statistics / members / details ----------

function Stats({ tr, cap }: { tr: TrafficResponse; cap: number | null }) {
  const { t, locale } = useI18n();
  const st = tr.stats;
  const enough = st.samples > 0;
  const row = (dir: "in" | "out") => {
    const d = st[dir];
    const cur = dir === "in" ? tr.port.rx_bps : tr.port.tx_bps;
    const prev = tr.compare?.stats[dir];
    return (
      <tr className="border-b border-rule/50 last:border-b-0">
        <td className={cn("py-1.5 pr-2 text-[11px] font-semibold tracking-[0.1em] uppercase", dir === "in" ? "text-signal" : "text-violet-300")}>{dir === "in" ? "↓" : "↑"} {t(`tr.short.${dir}` as Key)}</td>
        <td className="data py-1.5 pr-2 text-right text-slate-100">{formatBps(cur)}</td>
        <td className="data py-1.5 pr-2 text-right text-slate-300">{formatBps(d.avg)}</td>
        <td className="data py-1.5 pr-2 text-right text-slate-100" title={d.peak_at ? fmtDateTime(locale, d.peak_at) : undefined}>
          {formatBps(d.peak)}
          {prev?.peak && d.peak ? <span className={cn("block text-[10.5px]", d.peak >= prev.peak ? "text-signal-warn" : "text-signal-good")}>{d.peak >= prev.peak ? "+" : ""}{(((d.peak - prev.peak) / prev.peak) * 100).toFixed(1)}%</span> : null}
        </td>
        <td className="data py-1.5 text-right text-slate-300" title={d.p95 === null && enough ? t("tr.p95Withheld", { pct: Math.round(st.coverage * 100) }) : undefined}>
          {d.p95 === null ? <span className="text-slate-600">{enough ? t("tr.insufficient") : "—"}</span> : formatBps(d.p95)}
        </td>
      </tr>
    );
  };
  return (
    <div className="space-y-3 text-[12.5px]">
      <table className="w-full table-fixed whitespace-nowrap">
        <thead><tr className="text-left text-[10px] tracking-[0.12em] text-slate-500 uppercase">
          <th className="w-14 pb-1 font-medium" />
          {(["tr.cur", "tr.avg", "tr.peak", "tr.p95"] as Key[]).map((k) => <th key={k} className="pb-1 text-right font-medium">{t(k)}</th>)}
        </tr></thead>
        <tbody>{row("in")}{row("out")}</tbody>
      </table>
      <div>
        <div className="eyebrow mb-1 text-[10px]">{t("tr.transferred", { win: tr.window })}</div>
        <div className="grid grid-cols-3 gap-2">
          {([["in", st.in.bytes], ["out", st.out.bytes], ["total", st.in.bytes !== null || st.out.bytes !== null ? (st.in.bytes ?? 0) + (st.out.bytes ?? 0) : null]] as [string, number | null][]).map(([k, v]) => (
            <div key={k}><div className="text-[10.5px] text-slate-500">{t(`tr.bytes.${k}` as Key)}</div><div className="data text-[14px] text-slate-100">{fmtBytes(v)}</div></div>
          ))}
        </div>
      </div>
      <p className="text-[11px] leading-snug text-slate-500">
        {enough
          ? t("tr.coverage", { n: fmtNum(locale, st.samples), exp: fmtNum(locale, st.expected), pct: Math.round(st.coverage * 100), since: st.first_at ? fmtDateTime(locale, st.first_at).replace(/:\d\d$/, "") : "—" })
          : t("tr.noSamples")}
        {cap && st.in.peak ? <> · {t("tr.peakShare", { pct: ((Math.max(st.in.peak ?? 0, st.out.peak ?? 0) / cap) * 100).toFixed(1) })}</> : null}
      </p>
    </div>
  );
}

function Members({ lag }: { lag: NonNullable<TrafficResponse["lag"]> }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(lag.up < lag.total);
  return (
    <section className="rounded-lg border border-rule bg-panel">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-4 py-2 text-left">
        {open ? <FiChevronDown className="text-slate-500" /> : <FiChevronRight className="text-slate-500" />}
        <span className="eyebrow">{t("tr.lagMembers")}</span>
        <span className={cn("data text-[12px]", lag.up < lag.total ? "text-signal-warn" : "text-slate-400")}>{t("tr.membersUp", { up: lag.up, total: lag.total })}</span>
        <span className="data ml-auto text-[12px] text-slate-500">{formatBps(lag.capacity_up_bps)} / {formatBps(lag.capacity_total_bps)}</span>
      </button>
      {open && (
        <table className="w-full border-t border-rule text-[12.5px]">
          <tbody>
            {lag.members.map((m) => {
              const up = m.oper === "up";
              return (
                <tr key={m.port_id} className={cn("border-b border-rule/50 last:border-b-0", !up && "bg-signal-crit/[0.06]")}>
                  <td className="data px-4 py-1.5 text-slate-100">{m.ifname}</td>
                  <td className="data py-1.5 text-slate-500">{formatBps(m.speed_bps)}</td>
                  <td className="py-1.5"><span className={cn("text-[11px] font-semibold tracking-[0.1em] uppercase", up ? "text-signal-good" : "text-signal-crit")}>● {m.oper ?? "?"}</span></td>
                  <td className="data px-4 py-1.5 text-right text-slate-400">{up ? `↓${formatBps(m.rx_bps)} ↑${formatBps(m.tx_bps)}` : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Details({ tr, state }: { tr: TrafficResponse; state: PortState }) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const p = tr.port;
  const kv: [string, ReactNode][] = [
    [t("tr.d.device"), <>{tr.device.name} <span className="data text-slate-500">· {tr.device.hostname}</span></>],
    [t("tr.interface"), <span className="data">{p.ifname}</span>],
    [t("tr.d.alias"), p.ifalias ?? "—"],
    [t("tr.d.speed"), <span className="data">{formatBps(p.speed_bps)}</span>],
    [t("tr.d.type"), <span className="data">{p.if_type ?? "—"}</span>],
    [t("tr.d.status"), <>{t(`tr.state.${state}` as Key)} <span className="text-slate-500">· admin {p.admin ?? "?"} / oper {p.oper ?? "?"}</span></>],
    [t("tr.d.polled"), p.polled_at ? <span title={fmtDateTime(locale, p.polled_at)}>{fmtDateTime(locale, p.polled_at)}</span> : "—"],
    [t("tr.d.source"), "LibreNMS"],
  ];
  return (
    <section className="rounded-lg border border-rule bg-panel">
      <div className="flex items-center gap-2 px-4 py-2">
        <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-2 text-left">
          {open ? <FiChevronDown className="text-slate-500" /> : <FiChevronRight className="text-slate-500" />}
          <span className="eyebrow">{t("tr.details")}</span>
        </button>
        <span className="ml-auto flex gap-2">
          <Link to={`/netmap?focus=${encodeURIComponent(tr.device.key)}`} className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-signal"><FiShare2 /> {t("topo.viewMap")}</Link>
          <Link to={`/topology?locate=${encodeURIComponent(tr.device.key)}`} className="text-[12px] text-slate-400 hover:text-signal">{t("tr.viewTopology")}</Link>
        </span>
      </div>
      {open && (
        <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-3 gap-y-1 border-t border-rule px-4 py-2.5 text-[12.5px]">
          {kv.map(([k, v]) => <div key={k} className="contents"><dt className="text-slate-500">{k}</dt><dd className="min-w-0 truncate text-slate-200">{v}</dd></div>)}
        </dl>
      )}
    </section>
  );
}
